// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useState, useEffect } from 'react';

import { type FacilityId, useEvolvePaginatedGet, useEvolvePost } from '@evolvemep/api-ts';
import { Button, Text, Loader } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal, useWrappedModal } from 'components/Mantine/Modals/WrappedModal';
import type { EntityState } from 'components/Mantine/StatusEditor/types';

const WorkCellTasksPauseAllContent = ({ taskStatusTypes, facilityId }: Props) => {
  const { setDisabled } = useWrappedModal();
  const startedStatusTypeId = useMemo(
    () => taskStatusTypes.find(({ name }) => name === 'Started')?.stateId,
    [taskStatusTypes],
  );
  const { entireCount, loading, fetchNextPage } = useEvolvePaginatedGet('shop/workCellTask', {
    perPage: 1,
    defaultConfig: { params: { facilityIds: facilityId, taskStatusTypeIds: startedStatusTypeId } },
  });

  useEffect(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const noTasks = entireCount === 0;

  useEffect(() => {
    setDisabled(noTasks || loading);
  }, [loading, noTasks, setDisabled]);

  if (loading) return <Loader />;

  return (
    <Text>
      {noTasks ? (
        <>There are currently no tasks running.</>
      ) : (
        <>
          Are you sure you want to pause <b>{entireCount ?? 'all'} running tasks</b>?
        </>
      )}
    </Text>
  );
};

type Props = {
  facilityId: FacilityId;
  refresh?: () => void;
  taskStatusTypes: EntityState<'Task'>[];
};

export const WorkCellTasksPauseAll = (props: Props) => {
  const { facilityId, refresh } = props;
  const [opened, setOpened] = useState(false);
  const { apiCall } = useEvolvePost('shop/task/pauseAll');

  return (
    <>
      <Button variant="light" onClick={() => setOpened(true)} leftSection={<EvolveIcon icon="Pause" size="sm" />}>
        Pause all tasks
      </Button>

      <WrappedModal
        title="Pause all running tasks"
        opened={opened}
        confirmationText="Pause"
        onClose={() => setOpened(false)}
        onConfirm={async () => {
          await apiCall({ facilityId }).then(refresh);
        }}
      >
        <WorkCellTasksPauseAllContent {...props} />
      </WrappedModal>
    </>
  );
};
